/* eslint-disable @typescript-eslint/camelcase */
import { AccountController } from "@/app/ui/controllers/AccountController";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { updateUnleashContext } from "@/feature-flags/unleash/index";
import router from "@/app/ui/router";
import store from "@/store";
import { AccountEntities, AccountTypeDetail } from "@/domain/entities/Account";

// Enhanced helper functions using existing entities
const getAccountTypeDetail = (
  accountTypeDetail?: AccountTypeDetail
): string => {
  return accountTypeDetail?.type || "";
};

const getAccountCode = (accountTypeDetail?: AccountTypeDetail): string => {
  return accountTypeDetail?.code || "";
};

const getIsAccountParent = (accountTypeDetail?: AccountTypeDetail): boolean => {
  return !accountTypeDetail?.parentId;
};

const getIsCodDelivery = (accountTypeDetail?: AccountTypeDetail): boolean => {
  return accountTypeDetail?.isCodDelivery || false;
};

const getAccountCountry = (accountTypeDetail?: AccountTypeDetail): string => {
  return accountTypeDetail?.countryCode || "";
};

const extractAccountTypeData = (accountData: AccountEntities) => {
  const accountTypeDetail = accountData.account_type_detail;
  const countryCode =
    accountData.country_code || getAccountCountry(accountTypeDetail) || "";

  return {
    account_type_detail: getAccountTypeDetail(accountTypeDetail),
    account_code: getAccountCode(accountTypeDetail),
    is_account_parent: getIsAccountParent(accountTypeDetail),
    is_cod_delivery: getIsCodDelivery(accountTypeDetail),
    account_country: countryCode
  };
};

const extractLocationData = (accountData: AccountEntities) => {
  return {
    account_location: accountData.account_location?.city_code || ""
  };
};

const extractTimeData = () => {
  const now = new Date();
  return {
    randomize_property: now.toLocaleString(),
    hour_accessed: now.getHours().toString()
  };
};

const extractRouteData = () => {
  return {
    current_feature_accessed:
      router?.currentRoute?.value?.name?.toString() ?? ""
  };
};

const buildUnleashContext = (accountData: AccountEntities) => {
  const accountTypeData = extractAccountTypeData(accountData);
  const locationData = extractLocationData(accountData);
  const timeData = extractTimeData();
  const routeData = extractRouteData();

  return {
    email: accountData.email,
    role_type: accountData.role_type,
    account_type: accountData.account_type,
    username: accountData.username,
    client_id: accountData.account_type_detail?.id?.toString() || "",
    hub_name: getHubData()?.hubName || "",
    is_master: accountData.isMaster,
    ...accountTypeData,
    ...locationData,
    ...routeData,
    ...timeData
  };
};

// Function to update Unleash context with user data
export const updateUnleashWithAccountData = () => {
  try {
    if (!AccountController.accountData) return;

    const context = buildUnleashContext(AccountController.accountData);

    updateUnleashContext(context);
  } catch (error) {
    console.warn("Error updating Unleash context with account data:", error);
  }
};

// Set up a watcher for the Account store
let isWatcherSetup = false;

export const setupAccountWatcher = () => {
  if (
    !process.env.VUE_APP_FCS_OPENFEATURE_URL ||
    !process.env.VUE_APP_FCS_OPENFEATURE_CLIENT_KEY ||
    !process.env.VUE_APP_FCS_OPENFEATURE_APP_NAME
  )
    return;
  if (isWatcherSetup) return;

  // Watch for changes in the Account module
  store.subscribe((mutation) => {
    // Check if the mutation is from the Account module
    if (mutation.type.startsWith("Account/")) {
      // Update the context whenever the Account module changes
      updateUnleashWithAccountData();
    }
  });

  // Also update context on route changes to track current_feature_accessed
  router?.afterEach(() => {
    updateUnleashWithAccountData();
  });

  isWatcherSetup = true;
};

// Call updateUnleashWithAccountData once on initial load if account data is already available
if (AccountController?.accountData && AccountController?.accountData?.email) {
  updateUnleashWithAccountData();
}
